// SPDX-License-Identifier: AGPL-3.0-or-later

import Config from '@app/features/app/config/Config';
import {DESKTOP_DOWNLOAD_URL} from '@app/features/app/config/I18nDisplayConstants';
import type {UpdaterEvent} from '@app/features/platform/types/electron';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getClientInfo} from '@app/features/platform/utils/clientInfo';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {getElectronAPI, isElectron, openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {
	pushUnsupportedUpdateModal,
	pushUpdateAvailableModal,
	pushUpToDateModal,
} from '@app/features/updater/commands/UpdaterModalCommands';
import {t} from '@lingui/core/macro';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('Updater');
const CHECK_INTERVAL_MS = 30 * 60 * 1000;
const MIN_CHECK_INTERVAL_MS = 60 * 1000;
const VERSION_ENDPOINT = '/version.json';
const CURRENT_BUILD_SHA = Config.PUBLIC_BUILD_SHA ?? null;
const ALLOWED_WEB_UPDATE_HOSTS = new Set(['web.fluxer.app', 'web.canary.fluxer.app']);
export type UpdaterState = 'idle' | 'checking' | 'available';
export type UpdateType = 'native' | 'web' | 'both' | null;
export interface NativeUpdateInfo {
	available: boolean;
	downloaded: boolean;
	downloading: boolean;
	installing: boolean;
	version: string | null;
	downloadSize: number | null;
}
export interface NativeDownloadProgress {
	percent: number;
	transferred: number;
	total: number;
	bytesPerSecond: number;
}
export interface WebUpdateInfo {
	available: boolean;
	sha: string | null;
	buildNumber: number | null;
}
export interface UpdateInfo {
	native: NativeUpdateInfo;
	web: WebUpdateInfo;
}
class Updater {
	updateType: UpdateType = null;
	updateInfo: UpdateInfo = {
		native: {
			available: false,
			downloaded: false,
			downloading: false,
			installing: false,
			version: null,
			downloadSize: null,
		},
		web: {available: false, sha: null, buildNumber: null},
	};
	downloadProgress: NativeDownloadProgress | null = null;
	lastCheckedAt: number | null = null;
	currentVersion: string | null = null;
	channel: string | null = null;
	nativeUnsupported: {
		reason: 'platform' | 'unpackaged';
		downloadUrl: string | null;
	} | null = null;
	nativeManualDownloadUrl: string | null = null;
	private _isChecking = false;
	private isNative: boolean;
	private backgroundCheckStarted = false;
	private unsubscribeNativeEvents: (() => void) | null = null;
	private checkInProgress = false;
	private currentCheckUserInitiated = false;
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.isNative = isElectron();
		void this.bootstrap();
	}
	get hasUpdate(): boolean {
		return this.updateInfo.native.available || this.updateInfo.web.available;
	}
	get nativeUpdatePending(): boolean {
		return this.updateInfo.native.available && !this.updateInfo.native.downloaded;
	}
	get nativeUpdateReady(): boolean {
		return this.updateInfo.native.available && this.updateInfo.native.downloaded;
	}
	get nativeDownloadInFlight(): boolean {
		return this.updateInfo.native.downloading && !this.updateInfo.native.downloaded;
	}
	get nativeAwaitingDownload(): boolean {
		if (this.nativeManualDownloadUrl) return false;
		return (
			this.updateInfo.native.available && !this.updateInfo.native.downloaded && !this.updateInfo.native.downloading
		);
	}
	get state(): UpdaterState {
		if (this._isChecking) return 'checking';
		if (this.hasUpdate) return 'available';
		return 'idle';
	}
	get isChecking(): boolean {
		return this._isChecking;
	}
	get displayVersion(): string | null {
		if (this.updateInfo.native.available && this.updateInfo.native.version) {
			return this.updateInfo.native.version;
		}
		if (this.updateInfo.web.available) {
			if (this.updateInfo.web.buildNumber) {
				return `Build ${this.updateInfo.web.buildNumber}`;
			}
			return this.updateInfo.web.sha?.slice(0, 7) ?? null;
		}
		return null;
	}
	private refreshUpdateType(): void {
		const hasNative = this.updateInfo.native.available;
		const hasWeb = this.updateInfo.web.available;
		if (hasNative && hasWeb) {
			this.updateType = 'both';
		} else if (hasNative) {
			this.updateType = 'native';
		} else if (hasWeb) {
			this.updateType = 'web';
		} else {
			this.updateType = null;
		}
	}
	private async bootstrap(): Promise<void> {
		if (this.isNative) {
			await this.bootstrapNative();
		}
		this.startBackgroundChecks();
		void this.checkForUpdates(false);
	}
	private async bootstrapNative(): Promise<void> {
		try {
			const info = await getClientInfo();
			runInAction(() => {
				this.currentVersion = info.desktopVersion ?? null;
				this.channel = info.desktopChannel ?? null;
			});
		} catch (error) {
			logger.warn('Failed to read desktop info', error);
		}
		this.subscribeToNativeEvents();
	}
	private subscribeToNativeEvents(): void {
		const electronApi = getElectronAPI();
		if (!electronApi) return;
		this.unsubscribeNativeEvents = electronApi.onUpdaterEvent((event) => {
			this.handleNativeEvent(event as unknown as UpdaterEvent);
		});
	}
	private handleNativeEvent(event: UpdaterEvent): void {
		const isUserCheck = event.context === 'user';
		const isBackgroundOrFocusCheck = event.context === 'background' || event.context === 'focus';
		switch (event.type) {
			case 'checking':
				runInAction(() => {
					this._isChecking = true;
				});
				break;
			case 'available': {
				const downloadStarted = event.downloadStarted ?? true;
				const manualDownloadUrl = event.downloadUrl ?? null;
				runInAction(() => {
					this.updateInfo.native = {
						available: true,
						downloaded: false,
						downloading: downloadStarted,
						installing: false,
						version: event.version ?? null,
						downloadSize: event.downloadSize ?? null,
					};
					if (!downloadStarted) {
						this.downloadProgress = null;
					}
					this.nativeManualDownloadUrl = manualDownloadUrl;
					this.refreshUpdateType();
					this._isChecking = false;
				});
				if (isUserCheck) {
					if (manualDownloadUrl) {
						pushUpdateAvailableModal(event.version ?? null, manualDownloadUrl);
					} else {
						const message = downloadStarted
							? event.version
								? t`Update available — version ${event.version} is downloading.`
								: t`Update available — downloading in the background.`
							: event.version
								? t`Update available — version ${event.version} is ready to download.`
								: t`Update available — ready to download.`;
						ToastCommands.createToast({
							type: 'success',
							children: message,
							timeout: 5000,
						});
					}
				}
				break;
			}
			case 'not-available': {
				const hadDownloaded = this.updateInfo.native.downloaded;
				runInAction(() => {
					this.lastCheckedAt = Date.now();
					if (!hadDownloaded) {
						this.updateInfo.native = {
							available: false,
							downloaded: false,
							downloading: false,
							installing: false,
							version: null,
							downloadSize: null,
						};
						this.downloadProgress = null;
						this.nativeManualDownloadUrl = null;
						this.refreshUpdateType();
					}
					this._isChecking = false;
				});
				if (isUserCheck && !hadDownloaded && !this.updateInfo.web.available) {
					pushUpToDateModal(this.currentVersion);
				}
				break;
			}
			case 'error':
				if (isBackgroundOrFocusCheck) {
					logger.debug('Background update check failed silently:', event.message);
				} else {
					logger.warn('Update check error:', event.message);
				}
				runInAction(() => {
					this._isChecking = false;
					this.checkInProgress = false;
					this.updateInfo.native.downloading = false;
					this.updateInfo.native.installing = false;
					this.downloadProgress = null;
				});
				if (isUserCheck) {
					ToastCommands.createToast({
						type: 'error',
						children: t`Couldn't check for updates. Please try again later.`,
						timeout: 5000,
					});
				}
				break;
			case 'downloaded':
				runInAction(() => {
					this.updateInfo.native = {
						available: true,
						downloaded: true,
						downloading: false,
						installing: false,
						version: event.version ?? null,
						downloadSize: this.updateInfo.native.downloadSize,
					};
					this.downloadProgress = null;
					this.refreshUpdateType();
					this._isChecking = false;
				});
				if (isUserCheck) {
					ToastCommands.createToast({
						type: 'success',
						children: event.version
							? t`Update ready — version ${event.version} will install on restart.`
							: t`Update ready — restart to install.`,
						timeout: 6000,
					});
				}
				break;
			case 'progress':
				runInAction(() => {
					this.updateInfo.native.downloading = true;
					this.downloadProgress = {
						percent: event.percent,
						transferred: event.transferred,
						total: event.total,
						bytesPerSecond: event.bytesPerSecond,
					};
				});
				break;
			case 'unsupported': {
				const downloadUrl = event.downloadUrl ?? DESKTOP_DOWNLOAD_URL;
				runInAction(() => {
					this.nativeUnsupported = {
						reason: event.reason ?? 'platform',
						downloadUrl,
					};
					this.lastCheckedAt = Date.now();
					this._isChecking = false;
					this.checkInProgress = false;
				});
				if (isUserCheck) {
					pushUnsupportedUpdateModal(event.reason ?? 'platform', downloadUrl);
				}
				break;
			}
		}
	}
	private startBackgroundChecks(): void {
		if (this.backgroundCheckStarted) return;
		this.backgroundCheckStarted = true;
		window.setInterval(() => {
			if (document.visibilityState === 'visible') {
				void this.checkForUpdates(false);
			}
		}, CHECK_INTERVAL_MS);
		window.addEventListener('focus', () => void this.checkForUpdates(false));
		window.addEventListener('online', () => void this.checkForUpdates(true));
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				void this.checkForUpdates(false);
			}
		});
	}
	private shouldThrottle(force: boolean): boolean {
		if (force) return false;
		if (this.lastCheckedAt == null) return false;
		return Date.now() - this.lastCheckedAt < MIN_CHECK_INTERVAL_MS;
	}
	private shouldRunNativeCheck(): boolean {
		if (!this.isNative) return false;
		if (this.nativeUnsupported && !this.currentCheckUserInitiated) return false;
		return !this.updateInfo.native.available;
	}
	async checkForUpdates(force = false, userInitiated = false): Promise<void> {
		if (this.checkInProgress) {
			if (userInitiated) {
				ToastCommands.createToast({
					type: 'info',
					children: t`Already checking for updates…`,
					timeout: 2000,
				});
			}
			return;
		}
		if (this.shouldThrottle(force)) {
			if (userInitiated) {
				if (this.hasUpdate && this.nativeManualDownloadUrl) {
					pushUpdateAvailableModal(this.updateInfo.native.version, this.nativeManualDownloadUrl);
				} else if (this.hasUpdate) {
					ToastCommands.createToast({
						type: 'success',
						children: t`Update available — restart or reload to apply.`,
						timeout: 4000,
					});
				} else if (this.nativeUnsupported) {
					const downloadUrl = this.nativeUnsupported.downloadUrl ?? DESKTOP_DOWNLOAD_URL;
					pushUnsupportedUpdateModal(this.nativeUnsupported.reason, downloadUrl);
				} else {
					pushUpToDateModal(this.currentVersion);
				}
			}
			return;
		}
		this.checkInProgress = true;
		this.currentCheckUserInitiated = userInitiated;
		runInAction(() => {
			this._isChecking = true;
		});
		if (userInitiated) {
			ToastCommands.createToast({
				type: 'info',
				children: t`Checking for updates…`,
				timeout: 2000,
			});
		}
		const checkContext: 'user' | 'background' = userInitiated ? 'user' : 'background';
		try {
			const shouldCheckNative = this.shouldRunNativeCheck();
			const [, webResult] = await Promise.all([
				shouldCheckNative ? this.checkNativeUpdate(checkContext) : Promise.resolve(null),
				this.checkWebUpdate(),
			]);
			const webAvailable = webResult?.available ?? false;
			runInAction(() => {
				this.lastCheckedAt = Date.now();
				this.updateInfo.web = {
					available: webAvailable,
					sha: webResult?.sha ?? null,
					buildNumber: webResult?.buildNumber ?? null,
				};
				this.refreshUpdateType();
			});
			if (userInitiated && !shouldCheckNative) {
				if (webAvailable) {
					ToastCommands.createToast({
						type: 'success',
						children: t`Update available — reload to apply.`,
						timeout: 5000,
					});
				} else if (this.updateInfo.native.available && this.nativeManualDownloadUrl) {
					pushUpdateAvailableModal(this.updateInfo.native.version, this.nativeManualDownloadUrl);
				} else if (this.updateInfo.native.available) {
					ToastCommands.createToast({
						type: 'success',
						children: this.updateInfo.native.downloaded
							? t`Update ready — restart to install.`
							: t`Update available — downloading in the background.`,
						timeout: 5000,
					});
				} else {
					pushUpToDateModal(this.currentVersion);
				}
			}
		} catch (err) {
			logger.debug('Update check failed silently:', err);
			if (userInitiated) {
				ToastCommands.createToast({
					type: 'error',
					children: t`Couldn't check for updates. Please try again later.`,
					timeout: 5000,
				});
			}
		} finally {
			runInAction(() => {
				this.lastCheckedAt = Date.now();
				this.checkInProgress = false;
				this._isChecking = false;
			});
			this.currentCheckUserInitiated = false;
		}
	}
	private async checkNativeUpdate(context: 'user' | 'background'): Promise<boolean> {
		const electronApi = getElectronAPI();
		if (!electronApi) return false;
		try {
			await electronApi.updaterCheck(context);
			return true;
		} catch (error) {
			logger.debug('Native update check failed silently:', error);
			return false;
		}
	}
	private async checkWebUpdate(): Promise<{
		available: boolean;
		sha: string | null;
		buildNumber: number | null;
	}> {
		if (!ALLOWED_WEB_UPDATE_HOSTS.has(window.location.host)) {
			return {available: false, sha: null, buildNumber: null};
		}
		try {
			const response = await fetch(VERSION_ENDPOINT, {
				cache: 'no-store',
				headers: {'Cache-Control': 'no-cache'},
			});
			if (!response.ok) {
				logger.debug('Version endpoint not available');
				return {available: false, sha: null, buildNumber: null};
			}
			const payload = (await response.json()) as {
				sha?: string;
				buildNumber?: number;
				buildTimestamp?: string;
			};
			const updateAvailable = Boolean(payload.sha && CURRENT_BUILD_SHA && payload.sha !== CURRENT_BUILD_SHA);
			return {
				available: updateAvailable,
				sha: payload.sha ?? null,
				buildNumber: payload.buildNumber ?? null,
			};
		} catch (error) {
			logger.debug('Failed to fetch version info silently:', error);
			return {available: false, sha: null, buildNumber: null};
		}
	}
	async applyUpdate(): Promise<void> {
		if (!this.hasUpdate) return;
		const electronApi = getElectronAPI();
		if (this.isNative && this.updateInfo.native.downloaded && electronApi) {
			if (this.updateInfo.native.installing) {
				logger.debug('Install already in progress; ignoring duplicate click.');
				return;
			}
			runInAction(() => {
				this.updateInfo.native.installing = true;
			});
			logger.info('Installing downloaded native update...');
			try {
				await electronApi.updaterInstall();
			} catch (error) {
				logger.warn('Native update install failed', error);
				runInAction(() => {
					this.updateInfo.native.installing = false;
				});
				ToastCommands.createToast({
					type: 'error',
					children: t`Couldn't start the update. Please try again.`,
					timeout: 5000,
				});
			}
			return;
		}
		if (this.isNative && this.nativeAwaitingDownload && electronApi?.updaterDownload) {
			await this.startNativeDownload();
			return;
		}
		if (this.isNative && this.nativeDownloadInFlight) {
			const percent = this.downloadProgress?.percent ?? 0;
			ToastCommands.createToast({
				type: 'info',
				children: t`Downloading update… ${Math.round(percent)}% complete.`,
				timeout: 3000,
			});
			return;
		}
		if (this.updateInfo.web.available) {
			logger.info('Applying web update, reloading...');
			window.location.reload();
			return;
		}
		if (this.isNative && this.updateInfo.native.available) {
			logger.info('Native update is available but not installable in-app; opening desktop downloads...');
			const url = this.nativeManualDownloadUrl ?? this.nativeUnsupported?.downloadUrl ?? DESKTOP_DOWNLOAD_URL;
			await openExternalUrl(url);
		}
	}
	async startNativeDownload(userInitiated = true): Promise<void> {
		const electronApi = getElectronAPI();
		if (!electronApi?.updaterDownload) return;
		if (!this.updateInfo.native.available) return;
		if (this.updateInfo.native.downloading || this.updateInfo.native.downloaded) return;
		runInAction(() => {
			this.updateInfo.native.downloading = true;
			this.downloadProgress = {
				percent: 0,
				transferred: 0,
				total: this.updateInfo.native.downloadSize ?? 0,
				bytesPerSecond: 0,
			};
		});
		if (userInitiated) {
			ToastCommands.createToast({
				type: 'info',
				children: t`Downloading update…`,
				timeout: 2500,
			});
		}
		try {
			await electronApi.updaterDownload(userInitiated ? 'user' : 'background');
		} catch (error) {
			logger.warn('Native update download failed', error);
			runInAction(() => {
				this.updateInfo.native.downloading = false;
				this.downloadProgress = null;
			});
			if (userInitiated) {
				ToastCommands.createToast({
					type: 'error',
					children: t`Couldn't download the update. Please try again later.`,
					timeout: 5000,
				});
			}
		}
	}
	reset(): void {
		runInAction(() => {
			this.updateType = null;
			this.updateInfo = {
				native: {
					available: false,
					downloaded: false,
					downloading: false,
					installing: false,
					version: null,
					downloadSize: null,
				},
				web: {available: false, sha: null, buildNumber: null},
			};
			this.downloadProgress = null;
			this.lastCheckedAt = null;
			this._isChecking = false;
			this.checkInProgress = false;
			this.nativeManualDownloadUrl = null;
			this.refreshUpdateType();
		});
	}
	dispose(): void {
		if (this.unsubscribeNativeEvents) {
			this.unsubscribeNativeEvents();
			this.unsubscribeNativeEvents = null;
		}
	}
}
export default new Updater();
